/**
 * GuestAccumulation — Iter 51 UI shown to logged-out guests using the scanner.
 *
 *   1. CounterLine — visible from scan 1: "X of 5 free scans used"
 *   2. MealsStrip  — visible from scan 2+: horizontal thumbnails of prior scans
 *   3. SummaryCard — shown after the 5th scan completes: totals across all 5
 *                    (POST-multiplier) with a CTA to sign up and keep them.
 *
 * All macros in `pendingFoodScans` are stored POST-multiplier by
 * FoodScanResults, so summing them here is a straight arithmetic operation.
 */
import { useMemo } from "react";
import { ChevronRight } from "lucide-react";

export function CounterLine({ used, cap = 5, budgetReached = false }) {
  const remaining = Math.max(0, cap - used);
  if (budgetReached) {
    return (
      <div
        className="mb-3 rounded-[10px] px-3 py-2 text-[12px] font-medium"
        data-testid="counter-budget-reached"
        style={{
          background: "rgba(232,72,72,0.12)",
          color: "#B03030",
          border: "1px solid rgba(232,72,72,0.28)",
        }}
      >
        Free scans are at capacity today. Create a free account to keep scanning.
      </div>
    );
  }
  return (
    <div
      className="mb-3 flex items-center gap-2 text-[12px] font-medium"
      data-testid="counter-line"
      style={{ color: "#4A4845" }}
    >
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: cap }).map((_, i) => (
          <span
            key={i}
            className="w-2 h-2 rounded-full"
            style={{ background: i < used ? "#0FB5A6" : "#E2E0DC" }}
          />
        ))}
      </div>
      <span>
        {used} of {cap} free scans used
        {remaining > 0 && used > 0 ? ` · ${remaining} left` : ""}
      </span>
    </div>
  );
}

export function MealsStrip({ scans, onOpen }) {
  if (!scans || scans.length < 2) return null;
  return (
    <div
      className="mb-4"
      data-testid="meals-strip"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[12px] font-semibold" style={{ color: "#6B6760" }}>
          Your recent scans
        </div>
      </div>
      <div
        className="flex gap-2 overflow-x-auto pb-1"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {scans.map((s, i) => (
          <button
            key={s.client_scan_id || i}
            type="button"
            onClick={() => onOpen?.(s)}
            data-testid={`meal-strip-item-${i}`}
            className="shrink-0 rounded-[12px] overflow-hidden bg-white border"
            style={{
              width: 88,
              borderColor: "#E2E0DC",
              textAlign: "left",
            }}
          >
            <div
              className="w-full"
              style={{
                height: 64,
                background: s.thumbnail_base64
                  ? `center/cover no-repeat url("${s.thumbnail_base64}")`
                  : "linear-gradient(135deg,#F5F3EE,#E8E5DC)",
              }}
            />
            <div className="px-2 py-1.5">
              <div
                className="text-[11px] font-semibold truncate"
                style={{ color: "#1A1A1A" }}
              >
                {s.dish_name || "Scan"}
              </div>
              <div className="text-[10px]" style={{ color: "#8E8A84" }}>
                {Math.round(s.calories || 0)} kcal
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SummaryCard({ scans, onSignUp }) {
  const totals = useMemo(() => {
    const t = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };
    for (const s of scans || []) {
      t.calories += Number(s.calories) || 0;
      t.protein_g += Number(s.protein_g) || 0;
      t.carbs_g += Number(s.carbs_g) || 0;
      t.fat_g += Number(s.fat_g) || 0;
    }
    return t;
  }, [scans]);

  return (
    <div
      className="mb-4 rounded-[16px] p-4"
      data-testid="scan-summary-card"
      style={{
        background: "linear-gradient(135deg, rgba(0,212,255,0.10), rgba(15,181,166,0.08))",
        border: "1px solid rgba(0,176,213,0.30)",
      }}
    >
      <div
        className="font-barlow-cond"
        style={{ fontSize: 20, fontWeight: 800, color: "#1A1A1A", lineHeight: 1.1 }}
      >
        {scans.length} scans · {Math.round(totals.calories)} kcal
      </div>
      <p className="text-[12px] mt-0.5" style={{ color: "#4A4845" }}>
        Post-portion totals across your {scans.length} free scans
      </p>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {[
          { label: "Protein", val: totals.protein_g, tone: "#0FB5A6" },
          { label: "Carbs", val: totals.carbs_g, tone: "#F5A524" },
          { label: "Fat", val: totals.fat_g, tone: "#E85B48" },
        ].map((m) => (
          <div
            key={m.label}
            className="rounded-[10px] bg-white/70 px-2 py-1.5 text-center"
            data-testid={`summary-macro-${m.label.toLowerCase()}`}
          >
            <div className="text-[10px]" style={{ color: "#6B6760" }}>{m.label}</div>
            <div className="text-[14px] font-bold" style={{ color: m.tone }}>
              {Math.round(m.val)}g
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onSignUp}
        data-testid="summary-signup"
        className="mt-3 w-full h-11 rounded-pill flex items-center justify-center gap-1 text-[13px]"
        style={{
          background: "#00D4FF",
          color: "#000",
          borderRadius: 100,
          fontWeight: 700,
        }}
      >
        Save all 5 scans <ChevronRight size={15} />
      </button>
    </div>
  );
}
